// header //
class HeaderComponent extends HTMLElement {
  constructor() {
    super();

    this.lastScrollTop = 0;
    this.isTicking = false;
    this.stickyMode = this.getAttribute("sticky") || "always";
    this.headerHeight = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.handleResizeObserved = this.handleResizeObserved.bind(this);
  }

  connectedCallback() {
    this.setAttribute("data-scroll-direction", "down");

    this.headerInner = this.querySelector(".main-header");
    if (!this.headerInner) return;

    this.resizeObserver = new ResizeObserver(this.handleResizeObserved);
    this.resizeObserver.observe(this.headerInner);

    this.updateMeasurements();
    this.updateCSSVar();
    this.updateState();

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  handleResizeObserved() {
    this.updateMeasurements();
    this.updateCSSVar();
  }

  onResize() {
    this.updateMeasurements();
    this.updateCSSVar();
    this.updateState();
  }

  onScroll() {
    if (this.isTicking) return;

    this.isTicking = true;

    window.requestAnimationFrame(() => {
      this.updateState();
      this.isTicking = false;
    });
  }

  updateMeasurements() {
    if (!this.headerInner) return;
    this.headerHeight = this.headerInner.offsetHeight || 0;
  }

  updateCSSVar() {
    document.documentElement.style.setProperty(
      "--header-height",
      `${this.headerHeight}px`,
    );
  }

  updateState() {
    const currentScroll = Math.max(
      window.scrollY || window.pageYOffset || 0,
      0,
    );
    const direction = currentScroll > this.lastScrollTop ? "down" : "up";

    this.setAttribute("data-scroll-direction", direction);

    if (this.stickyMode === "always") {
      if (currentScroll > 0) {
        this.setAttribute("data-sticky-state", "active");
      } else {
        this.removeAttribute("data-sticky-state");
      }
    }

    if (this.stickyMode === "on-scroll") {
      if (currentScroll > this.headerHeight) {
        this.setAttribute("data-sticky-state", "active");
      } else {
        this.removeAttribute("data-sticky-state");
      }
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

// header mobile menu //
class HeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.openButton = this.querySelector("[data-mobile-menu-open]");
    this.closeButton = this.querySelector("[data-mobile-menu-close]");
    this.drawer = this.querySelector("[data-mobile-menu]");
    this.triggers = this.querySelectorAll("[data-mobile-menu-trigger]");
    this.backButtons = this.querySelectorAll("[data-mobile-menu-back]");

    this.handleEscape = this.handleEscape.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
  }

  connectedCallback() {
    if (!this.openButton || !this.closeButton || !this.drawer) return;
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.openButton.addEventListener("click", this.openMenu);
    this.closeButton.addEventListener("click", this.closeMenu);

    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        const item = trigger.closest("[data-mobile-menu-item]");
        const submenu = item?.querySelector("[data-mobile-menu-submenu]");

        if (!submenu) return;

        trigger.setAttribute("aria-expanded", "true");
        submenu.classList.remove("hidden");
        requestAnimationFrame(() => {
          submenu.classList.add("is-open");
        });
      });
    });

    this.backButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const submenu = button.closest("[data-mobile-menu-submenu]");
        const item = button.closest("[data-mobile-menu-item]");
        const trigger = item?.querySelector("[data-mobile-menu-trigger]");

        if (!submenu) return;

        submenu.classList.remove("is-open");

        setTimeout(() => {
          submenu.classList.add("hidden");
          if (trigger) trigger.setAttribute("aria-expanded", "false");
        }, 280);
      });
    });

    document.addEventListener("keydown", this.handleEscape);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleEscape);
  }

  handleEscape(event) {
    if (event.key !== "Escape") return;

    const openSubmenu = this.querySelector(
      "[data-mobile-menu-submenu].is-open",
    );

    if (openSubmenu) {
      openSubmenu.classList.remove("is-open");
      setTimeout(() => {
        openSubmenu.classList.add("hidden");
      }, 280);
      return;
    }

    if (this.drawer.classList.contains("is-open")) {
      this.closeMenu();
    }
  }

  openMenu() {
    this.drawer.classList.add("is-open");
    this.drawer.setAttribute("aria-hidden", "false");
    this.openButton.setAttribute("aria-expanded", "true");
    document.body.classList.add("menu-mobile-open", "overflow-hidden");
  }

  closeMenu() {
    this.drawer.classList.remove("is-open");
    this.drawer.setAttribute("aria-hidden", "true");
    this.openButton.setAttribute("aria-expanded", "false");

    this.querySelectorAll("[data-mobile-menu-submenu].is-open").forEach(
      (submenu) => {
        submenu.classList.remove("is-open");
        submenu.classList.add("hidden");
      },
    );

    this.querySelectorAll("[data-mobile-menu-trigger]").forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });

    document.body.classList.remove("menu-mobile-open", "overflow-hidden");
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

// header menu width for mega menu //
document.addEventListener("DOMContentLoaded", () => {
  const menu = document.querySelector(".header__menu-width");
  if (!menu) return;

  function setMenuWidth() {
    const width = menu.offsetWidth;
    document.documentElement.style.setProperty(
      "--header-menu-width",
      `${width}px`,
    );
  }

  setMenuWidth();

  window.addEventListener("load", setMenuWidth);
});

// accordion, data-oonly-mobile se true funziona solo da mobile

class AccordionComponent extends HTMLElement {
  constructor() {
    super();

    this.trigger = this.querySelector("[data-accordion-trigger]");
    this.content = this.querySelector("[data-accordion-content]");
    this.icon = this.querySelector("[data-accordion-icon]");

    this.onToggle = this.onToggle.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.content) return;
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.trigger.addEventListener("click", this.onToggle);
    window.addEventListener("resize", this.onResize);

    this.syncMode();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener("click", this.onToggle);
    window.removeEventListener("resize", this.onResize);
  }

  isOnlyMobile() {
    return this.dataset.onlyMobile === "true";
  }

  isDesktop() {
    return window.innerWidth >= 768;
  }

  setState(isOpen) {
    this.trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    this.content.classList.toggle("hidden", !isOpen);

    if (this.icon) {
      this.icon.classList.toggle("rotate-90", isOpen);
    }
  }

  syncMode() {
    const isDesktop = this.isDesktop();

    // sempre aperto (no accordion)
    if (!this.isOnlyMobile()) {
      this.setState(true);
      return;
    }

    if (isDesktop) {
      this.setState(true);

      if (this.icon) this.icon.classList.add("hidden");
    } else {
      this.setState(false);

      if (this.icon) this.icon.classList.remove("hidden");
    }
  }

  onResize() {
    this.syncMode();
  }

  onToggle() {
    if (this.isOnlyMobile() && this.isDesktop()) return;

    const isOpen = this.trigger.getAttribute("aria-expanded") === "true";
    this.setState(!isOpen);
  }
}

if (!customElements.get("accordion-component")) {
  customElements.define("accordion-component", AccordionComponent);
}

// megamenu
(() => {
  const menuItems = document.querySelectorAll(".header-menu-item");
  if (!menuItems.length) return;

  function closeAllMenus(exceptItem = null) {
    menuItems.forEach((item) => {
      if (item === exceptItem) return;

      const button = item.querySelector(".mega-menu-trigger");
      const panel = item.querySelector(".mega-menu");

      item.classList.remove("is-open");

      if (button) button.setAttribute("aria-expanded", "false");
      if (panel) panel.setAttribute("aria-hidden", "true");
    });
  }

  menuItems.forEach((item) => {
    const button = item.querySelector(".mega-menu-trigger");
    const panel = item.querySelector(".mega-menu");

    if (!button || !panel) return;

    let closeTimeout = null;

    function openMenu() {
      if (closeTimeout) clearTimeout(closeTimeout);
      closeAllMenus(item);

      item.classList.add("is-open");
      button.setAttribute("aria-expanded", "true");
      panel.setAttribute("aria-hidden", "false");
    }

    function scheduleClose() {
      if (closeTimeout) clearTimeout(closeTimeout);

      closeTimeout = setTimeout(() => {
        item.classList.remove("is-open");
        button.setAttribute("aria-expanded", "false");
        panel.setAttribute("aria-hidden", "true");
      }, 120);
    }

    function cancelClose() {
      if (closeTimeout) clearTimeout(closeTimeout);
    }

    item.addEventListener("mouseenter", openMenu);
    item.addEventListener("mouseleave", scheduleClose);

    panel.addEventListener("mouseenter", cancelClose);
    panel.addEventListener("mouseleave", scheduleClose);

    button.addEventListener("click", (event) => {
      const isOpen = item.classList.contains("is-open");
      const href = button.getAttribute("href");

      if (isOpen && href && href !== "#") {
        return;
      }

      event.preventDefault();
      openMenu();
    });

    button.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        scheduleClose();
        button.focus();
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        openMenu();
        const firstLink = panel.querySelector("a");
        if (firstLink) firstLink.focus();
      }
    });

    panel.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        scheduleClose();
        button.focus();
      }
    });
  });

  document.addEventListener("click", (event) => {
    if (!event.target.closest(".header-menu-item")) {
      closeAllMenus();
    }
  });
})();

// component drower

class SideDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerId = this.dataset.drawerId;
    this.overlay = this.querySelector("[data-drawer-overlay]");
    this.panel = this.querySelector("[data-drawer-panel]");
    this.openButtons = [];

    this.handleEscape = this.handleEscape.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    if (!this.overlay || !this.panel || !this.drawerId) return;
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.openButtons = document.querySelectorAll(
      `[data-drawer-open][aria-controls="${this.drawerId}"]`,
    );

    this.openButtons.forEach((button) => {
      button.addEventListener("click", this.open);
    });

    // Delegation: funziona anche con pulsanti sostituiti via AJAX (es. "Applica" nei filtri PLP)
    this.addEventListener("click", this.handleCloseClick);
    this.overlay.addEventListener("click", this.close);
    document.addEventListener("keydown", this.handleEscape);
  }

  disconnectedCallback() {
    this.openButtons.forEach((button) => {
      button.removeEventListener("click", this.open);
    });

    this.removeEventListener("click", this.handleCloseClick);
    this.overlay?.removeEventListener("click", this.close);
    document.removeEventListener("keydown", this.handleEscape);
  }

  handleCloseClick(event) {
    if (event.target.closest("[data-drawer-close]")) this.close();
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.classList.contains("is-open")) {
      this.close();
    }
  }

  open() {
    this.classList.add("is-open");
    this.panel.setAttribute("aria-hidden", "false");
    this.overlay.classList.remove("hidden");

    this.openButtons.forEach((button) => {
      button.setAttribute("aria-expanded", "true");
    });

    this.dispatchEvent(new CustomEvent("drawer:opened", { bubbles: true, detail: { drawerId: this.drawerId } }));
  }

  close() {
    this.classList.remove("is-open");
    this.panel.setAttribute("aria-hidden", "true");
    this.overlay.classList.add("hidden");

    this.openButtons.forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });

  }
}

if (!customElements.get("side-drawer")) {
  customElements.define("side-drawer", SideDrawer);
}

// card product swiper

class ProductCardSwiper extends HTMLElement {
  connectedCallback() {
    this._tryInit(0);
  }

  disconnectedCallback() {
    if (this._swiper) {
      this._swiper.destroy(true, true);
      this._swiper = null;
    }
  }

  _tryInit(attempts) {
    // Aspetta che il container abbia una larghezza reale (max 20 tentativi)
    const el = this.querySelector("[data-card-swiper]");
    if (!el) return;

    const width = this.getBoundingClientRect().width;

    if (width === 0 && attempts < 20) {
      requestAnimationFrame(() => this._tryInit(attempts + 1));
      return;
    }

    this._initSwiper(el);
  }

  _initSwiper(el) {
    const count = parseInt(el.dataset.cardSwiperCount, 10);
    if (!count || count <= 1) return;
    if (el.classList.contains("swiper-initialized")) return;

    const prevBtn = el.querySelector("[data-card-swiper-prev]");
    const nextBtn = el.querySelector("[data-card-swiper-next]");
    const progressBar = el.querySelector("[data-card-swiper-progress]");

    this._swiper = new Swiper(el, {
      loop: true,
      slidesPerView: 1,
      speed: 400,
      grabCursor: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,

      navigation:
        prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : false,

      on: {
        init: (s) => this._updateProgress(s, progressBar),
        slideChange: (s) => this._updateProgress(s, progressBar),
      },
    });

    // Forza ricalcolo dimensioni dopo l'init
    this._swiper.update();
  }

  _updateProgress(swiper, progressBar) {
    if (!progressBar) return;

    const total = parseInt(swiper.el.dataset.cardSwiperCount, 10);

    const index = swiper.realIndex;

    if (!total || total <= 1) return;

    const segment = 100 / total;

    progressBar.style.width = `${segment}%`;

    progressBar.style.transform = `translateX(${index * 100}%)`;
  }
}

customElements.define("product-card-swiper", ProductCardSwiper);




// cart drawer & cart page

async function refreshCartDrawer() {
  const drawerInner = document.querySelector('[data-cart-drawer-inner]');
  if (!drawerInner) return;

  // use the section instance id so customizer settings are kept on refresh
  const sectionId = drawerInner.dataset.sectionId || 'cart-drawer-section';
  const response = await fetch(`/?sections=${sectionId}`);
  const sections = await response.json();

  const html = sections[sectionId];
  if (!html) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  const newInner = doc.querySelector('[data-cart-drawer-inner]');
  const currentInner = document.querySelector('[data-cart-drawer-inner]');

  if (newInner && currentInner) {
    currentInner.replaceWith(newInner);
  }
}

async function refreshCartPage() {
  const cartContainer = document.querySelector('[data-cart-page]');
  if (!cartContainer) return;

  const response = await fetch('/cart?sections=cart');
  const sections = await response.json();

  const html = sections['cart'];
  if (!html) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const newCart = doc.querySelector('[data-cart-page]');

  if (newCart) {
    cartContainer.replaceWith(newCart);
  }
}

function openCartDrawer() {
  const drawer = document.querySelector('side-drawer[data-drawer-id="CartDrawer"]');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
}

async function updateCartLine(line, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({
      line: Number(line),
      quantity: Number(quantity)
    })
  });

  if (!response.ok) {
    throw new Error('Cart update failed');
  }

  await Promise.all([
    refreshCartDrawer(),
    refreshCartPage()
  ]);

  // Open the drawer only if you are not on the cart page
  if (!document.querySelector('[data-cart-page]')) {
    openCartDrawer();
  }

  document.dispatchEvent(new CustomEvent('cart:updated'));
}

document.addEventListener('click', async function (event) {
  const minusButton = event.target.closest('[data-cart-qty-minus]');
  const plusButton = event.target.closest('[data-cart-qty-plus]');
  const removeButton = event.target.closest('[data-cart-remove]');
  if (!minusButton && !plusButton && !removeButton) return;
  event.preventDefault();
  const button = minusButton || plusButton || removeButton;
  const line = button.dataset.line;
  if (!line) return;
  const itemWrapper = button.closest('[data-cart-item]');
  const quantityEl = itemWrapper?.querySelector('[data-cart-item-quantity]');
  const currentQuantity = parseInt(quantityEl?.textContent?.trim(), 10) || 1;

  button.disabled = true;

  try {
    if (removeButton) {
      await updateCartLine(line, 0);
      return;
    }

    if (minusButton) {
      await updateCartLine(line, Math.max(currentQuantity - 1, 0));
      return;
    }

    if (plusButton) {
      await updateCartLine(line, currentQuantity + 1);
      return;
    }

  } catch (error) {
    console.error('[Cart update error]', error);
  } finally {
    button.disabled = false;
  }
});



